import React, { FC } from "react";
import { useTheme } from "styled-components";
import { linkToHome } from "helpers/links-builders/home";
import IconAddPost from "assets/icons/IconAddPost";
import IconChat from "assets/icons/IconChat";
import IconNotifications from "assets/icons/IconNotifications";
import { HeaderTwoCont, RedBubble } from "./styled.styles";
import { gtmAddPostBtnHeader } from "GTMs/actions/commonActions";
import useGetRouteCity from "hooks/useGetRouteCity";
import CitiesSearchField from "componentLayout/Header/HeaderFour/HeaderFourDesktop/SearchFieldBar/CitiesSearchField";
import { classNames, labelHelper } from "helpers/themeHelpers";
import { IGoToFindParams } from "models/header";
import { ICityLiteAsset } from "models/Assets/ICityAsset";
import { AddPostProvider } from "../../../../componentReusable/Providers/AddPostProvider";
import { getIntegrationLink } from "helpers/links-builders/loggedInLinks";
import { EnumIntegrationEvent } from "../../../../enums/EnumIntegrationEvent";
import IconMyPosts from "assets/icons/IconMyPosts";
import { linkToMyOS } from "helpers/links-builders/linkToMyOS";
import { isChatEnabled, isShobiddak } from "helpers/osHelpers";
import dynamic from "next/dynamic";
import { useSsoContext } from "contexts/authSSOContext";
import { EnumWidgets } from "../../../../libs/logging/types/loggingTypes";
import { handleLoggingAuthenticate } from "../../../../libs/logging/helpers/shareLogging";
import Image from "componentReusable/Image";
import {MyProfileLinks} from "helpers/links-builders/myProfileLinks";

const NotificationCounterProvider = dynamic(
  () => import("libs/counterProviders/NotificationCounterProvider"),
  { suspense: false }
);

const ChatCounterProvider = dynamic(
  () => import("libs/counterProviders/ChatCounterProvider"),
  { suspense: false }
);

const HeaderTwoLoggedInDesktop = dynamic(
  () =>
    import(
      "componentLayout/Header/HeaderTwo/HeaderTwoDesktop/HeaderTwoLoggedInDesktop"
    ),
  { suspense: false }
);

const NotificationModal = dynamic(
    () => import("components/NotificationModal"),
    { suspense: false }
    );

interface IProps {
  onChangeCityEvent?: (city: ICityLiteAsset) => void; // to be used for listing
}

const HeaderTwoDesktop: FC<IProps> = ({ onChangeCityEvent = null }) => {
  const { isLoggedInSso, loggedInToken, verifiedToken, userInfo } =
    useSsoContext();
  const { translations, country } = useTheme();
  const city = useGetRouteCity();
  const cityPlaceHolder = labelHelper(city);

  const goToFindCity = (params: IGoToFindParams) => {
    const linkToFindParams: any = { ...params };
    if (onChangeCityEvent) {
      onChangeCityEvent(params.city);
    } else {
      window.location.href = linkToHome(linkToFindParams.city);
    }
  };
  const [openModal, setOpenModal] = React.useState(false);
  const [eventData, setEventData] = React.useState(null);

  const goToMyOS = (url, withVerify = false) => {
    if (withVerify) {
      isLoggedInSso && userInfo.isNewUser === false
        ? verifiedToken().then(() => (window.location.href = url))
        : verifiedToken(() => (window.location.href = url)).then();
      return;
    }

    if (isLoggedInSso) {
      return window.open(url, "_blank");
    }

    isLoggedInSso && userInfo.isNewUser === false
      ? loggedInToken().then(() => (window.location.href = url))
      : loggedInToken(() => (window.location.href = url)).then();
  };

  const chooseLogo = ()=> {
    const allowedCountries = ["jo", "kw", "iq", "dz"];
    if (allowedCountries.includes(country)) {
      return `/images/openSooqLogo/blackLogo.svg`
    }
    return `/images/openSooqLogo/openSooqLogoDesktop.svg`
  }
  return (
    <header id="headerTwoDesktop" className="whiteBg borderBottomLightGray">
      <HeaderTwoCont className="flex flexSpaceBetween m-auto width-1200 pt-16 pb-16 ">
        <div className="flex alignItems gap-20">
          <div
            {...classNames(
              "flex alignItems gap-20",
              isShobiddak() && "flexDirectionRowReverse"
            )}
          >
            <a
              href={linkToHome(city)}
              className="logo alignSelfCenter"
              aria-label={"Logo"}
            >
              <Image
                src={chooseLogo()}
                width="124"
                height="44"
                alt={"OpenSooq - السوق المفتوح"}
                className="noImgFit"
              />
            </a>
            {isShobiddak() || country === "ps" ? (
              <a
                href={linkToHome(city)}
                className="logo alignSelfCenter"
                aria-label={"Logo"}
              >
                <Image
                  src="/images/shobiddakLogo/shobiddakLogoDesktop.svg"
                  width="124"
                  height="44"
                  alt={"OpenSooq - السوق المفتوح"}
                />
              </a>
            ) : null}
          </div>
          <CitiesSearchField
            cityPlaceHolder={cityPlaceHolder}
            goToFind={goToFindCity}
          />
        </div>
        <div className="flex justifyContent alignItems">
          <button
            className="bubbleLink relative noRipple font-12"
            aria-label="My Posts"
            onClick={() => {
              goToMyOS(linkToMyOS(MyProfileLinks.myHome.link));
              if (!isLoggedInSso) {
                handleLoggingAuthenticate({
                  currentWidgetName: EnumWidgets.HEADER,
                });
              }
            }}
          >
            <IconMyPosts width="17" height="24" />
          </button>
          <button
            className="bubbleLink relative noRipple font-12"
            aria-labelledby="Notifications label"
            onClick={() => {
              if (!isLoggedInSso) {
                handleLoggingAuthenticate({
                  currentWidgetName: EnumWidgets.HEADER,
                });
              }

              goToMyOS(linkToMyOS(MyProfileLinks.myNotifications.link));
            }}
          >
            {isLoggedInSso && (
              <>
                <NotificationCounterProvider
                  render={(count) => {
                    return (
                      <RedBubble
                        {...classNames(
                          "absolute inline fullRadius center whiteColor bold",
                          isLoggedInSso && count === 0 && "forceHide"
                        )}
                      >
                        {count > 99 ? "99+" : count}
                      </RedBubble>
                    );
                  }}
                  openModal={() => setOpenModal(true)}
                  eventData={(e) => setEventData(e)}
                />

                {openModal && eventData && (
                  <NotificationModal
                    notificationEvent={eventData}
                    openModal={openModal}
                  />
                )}
              </>
            )}

            {!isLoggedInSso && (
              <RedBubble className="absolute inline fullRadius center whiteColor">
                1
              </RedBubble>
            )}
            <IconNotifications
              width="20"
              height="25"
              fill="#000"
              className="relative"
            />
          </button>
          {isChatEnabled(country) && (
            <button
              className="bubbleLink relative noRipple font-12"
              aria-labelledby="chat label"
              onClick={() => {
                if (!isLoggedInSso) {
                  handleLoggingAuthenticate({
                    currentWidgetName: EnumWidgets.HEADER,
                  });
                }
                goToMyOS(linkToMyOS(MyProfileLinks.myChats.link), true);
              }}
            >
              {isLoggedInSso && (
                <ChatCounterProvider
                  render={(count) => {
                    return (
                      <RedBubble
                        {...classNames(
                          "absolute inline fullRadius center whiteColor bold",
                          isLoggedInSso && count === 0 && "forceHide"
                        )}
                      >
                        {count > 99 ? "99+" : count}
                      </RedBubble>
                    );
                  }}
                />
              )}
              {!isLoggedInSso && (
                <RedBubble className="absolute inline fullRadius center whiteColor">
                  1
                </RedBubble>
              )}
              <IconChat width="25" height="25" className="relative" />
            </button>
          )}
          <HeaderTwoLoggedInDesktop />
          <AddPostProvider
            gtmAction={gtmAddPostBtnHeader}
            iframeUrl={getIntegrationLink(EnumIntegrationEvent.ADD_POST)}
            widgetName={EnumWidgets.HEADER}
            render={({ onClickCb }) => (
              <button
                id="addPost_btn"
                className="addPostLink orangeBtn flex alignItems font-20 ms-16"
                onClick={onClickCb}
              >
                <IconAddPost
                  className="inline vMiddle me-8"
                  fill="#fff"
                  width="22"
                  height="22"
                />
                {translations.addPostM}
              </button>
            )}
          />
        </div>
      </HeaderTwoCont>
    </header>
  );
};

export default HeaderTwoDesktop;
